import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

// Aceite de convite. Rota publica: quem chega aqui ainda nao tem conta.
// A validacao do token e a criacao do usuario acontecem na Edge Function
// `aceitar-convite`, que e quem tem a chave de servico.

interface ConviteResumo {
  email: string;
  nome: string | null;
  papel: string;
  expiraEm: string | null;
}

const SENHA_MINIMA = 8;

export default function AceitarConvitePage() {
  const [token, setToken] = useState('');
  const [convite, setConvite] = useState<ConviteResumo | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [concluido, setConcluido] = useState(false);

  const [nome, setNome] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get('token') || '';
    setToken(t);

    if (!t) {
      setErro('Link de convite inválido: falta o token.');
      setCarregando(false);
      return;
    }

    void supabase.functions
      .invoke(`aceitar-convite?token=${encodeURIComponent(t)}`, { method: 'GET' })
      .then(({ data, error }) => {
        const resposta = data as { convite?: ConviteResumo; error?: string } | null;
        if (resposta?.convite) {
          setConvite(resposta.convite);
          setNome(resposta.convite.nome || '');
        } else {
          setErro(resposta?.error || error?.message || 'Convite não encontrado.');
        }
      })
      .catch(() => setErro('Não foi possível validar o convite.'))
      .finally(() => setCarregando(false));
  }, []);

  const aceitar = async (evento: React.FormEvent) => {
    evento.preventDefault();
    setErro(null);

    if (senha.length < SENHA_MINIMA) {
      setErro(`A senha precisa ter ao menos ${SENHA_MINIMA} caracteres.`);
      return;
    }
    if (senha !== confirmacao) {
      setErro('As senhas não conferem.');
      return;
    }

    setEnviando(true);
    const { data, error } = await supabase.functions.invoke('aceitar-convite', {
      body: { token, senha, nome },
    });
    const resposta = data as { ok?: boolean; error?: string } | null;
    setEnviando(false);

    if (resposta?.ok) {
      setConcluido(true);
      setTimeout(() => {
        window.location.href = '/login';
      }, 2500);
      return;
    }
    setErro(resposta?.error || error?.message || 'Não foi possível concluir o cadastro.');
  };

  if (carregando) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
          <p className="text-sm text-foreground-500 mt-3">Validando convite...</p>
        </div>
      </div>
    );
  }

  if (concluido) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 px-4">
        <div className="max-w-md w-full bg-background-100 border border-background-200/70 rounded-lg p-8 text-center">
          <i className="ri-checkbox-circle-line text-4xl text-primary-500"></i>
          <h1 className="text-lg font-bold text-foreground-900 mt-3">Conta criada</h1>
          <p className="text-sm text-foreground-600 mt-2">
            Tudo certo. Estamos levando você para a tela de login.
          </p>
        </div>
      </div>
    );
  }

  if (!convite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 px-4">
        <div className="max-w-md w-full bg-background-100 border border-background-200/70 rounded-lg p-8 text-center">
          <i className="ri-error-warning-line text-4xl text-red-500"></i>
          <h1 className="text-lg font-bold text-foreground-900 mt-3">Convite indisponível</h1>
          <p className="text-sm text-foreground-600 mt-2">{erro}</p>
          <a
            href="/login"
            className="inline-block mt-6 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold"
          >
            Ir para o login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-50 px-4 py-10">
      <div className="max-w-md w-full bg-background-100 border border-background-200/70 rounded-lg p-8">
        <h1 className="text-xl font-bold text-foreground-900">Aceitar convite</h1>
        <p className="text-sm text-foreground-600 mt-2">
          Você foi convidado como <strong>{convite.papel}</strong>. Defina uma senha para acessar o
          Central Check com <strong>{convite.email}</strong>.
        </p>

        <form onSubmit={aceitar} className="mt-6 flex flex-col gap-4">
          <div>
            <label htmlFor="nome" className="block text-sm font-medium text-foreground-800 mb-1.5">
              Seu nome
            </label>
            <input
              id="nome"
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm"
              placeholder="Como você quer ser chamado"
            />
          </div>

          <div>
            <label htmlFor="senha" className="block text-sm font-medium text-foreground-800 mb-1.5">
              Senha
            </label>
            <input
              id="senha"
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              autoComplete="new-password"
              className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm"
              placeholder={`Mínimo de ${SENHA_MINIMA} caracteres`}
              required
            />
          </div>

          <div>
            <label
              htmlFor="confirmacao"
              className="block text-sm font-medium text-foreground-800 mb-1.5"
            >
              Confirme a senha
            </label>
            <input
              id="confirmacao"
              type="password"
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
              autoComplete="new-password"
              className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm"
              required
            />
          </div>

          {erro ? (
            <div className="px-3 py-2.5 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
              {erro}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={enviando}
            className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60"
          >
            {enviando ? 'Criando conta...' : 'Criar minha conta'}
          </button>
        </form>
      </div>
    </div>
  );
}
